/** UI localization config — the 21 interface languages (mirrors the picker
 * on lingvanex.com). Codes match the SEO locale codes in
 * scripts/seo-locales/, so the two translation catalogs stay in sync. */

export interface UiLocale {
  code: string
  /** Autonym shown in the switcher */
  label: string
  /** English name — used by analytics (Locale Name) */
  english: string
  bcp47: string
  rtl?: boolean
}

export const UI_LOCALES: UiLocale[] = [
  { code: 'ar', label: 'العربية', english: 'Arabic', bcp47: 'ar', rtl: true },
  { code: 'de', label: 'Deutsch', english: 'German', bcp47: 'de' },
  { code: 'el', label: 'Ελληνικά', english: 'Greek', bcp47: 'el' },
  { code: 'en', label: 'English', english: 'English', bcp47: 'en' },
  { code: 'es', label: 'Español', english: 'Spanish', bcp47: 'es' },
  { code: 'fr', label: 'Français', english: 'French', bcp47: 'fr' },
  { code: 'hi', label: 'हिन्दी', english: 'Hindi', bcp47: 'hi' },
  { code: 'hu', label: 'Magyar', english: 'Hungarian', bcp47: 'hu' },
  { code: 'id', label: 'Bahasa Indonesia', english: 'Indonesian', bcp47: 'id' },
  { code: 'it', label: 'Italiano', english: 'Italian', bcp47: 'it' },
  { code: 'ja', label: '日本語', english: 'Japanese', bcp47: 'ja' },
  { code: 'ko', label: '한국어', english: 'Korean', bcp47: 'ko' },
  { code: 'nl', label: 'Nederlands', english: 'Dutch', bcp47: 'nl' },
  { code: 'pl', label: 'Polski', english: 'Polish', bcp47: 'pl' },
  { code: 'pt', label: 'Português', english: 'Portuguese', bcp47: 'pt' },
  { code: 'ru', label: 'Русский', english: 'Russian', bcp47: 'ru' },
  { code: 'tr', label: 'Türkçe', english: 'Turkish', bcp47: 'tr' },
  { code: 'uz', label: 'Oʻzbekcha', english: 'Uzbek', bcp47: 'uz' },
  { code: 'vi', label: 'Tiếng Việt', english: 'Vietnamese', bcp47: 'vi' },
  { code: 'zh-hans', label: '简体中文', english: 'Simplified Chinese', bcp47: 'zh-Hans' },
  { code: 'zh-hant', label: '繁體中文', english: 'Traditional Chinese', bcp47: 'zh-Hant' },
]

export const UI_LANG_STORAGE_KEY = 'stt_ui_lang'

/** The locale-home routes: /ru/, /de/, /zh-hans … (and bare /ru). These are
 * real pages — static localized shells generated at build time boot the SPA
 * in that language. Deeper locale paths belong to the static SEO pages and
 * never reach the router. */
const LOCALE_ROOT_RE = /^\/([a-z]{2,3}(?:-[a-z]+)?)\/?$/

export function pathLocale(pathname: string = window.location.pathname): string | null {
  const m = pathname.match(LOCALE_ROOT_RE)
  if (!m) return null
  return UI_LOCALES.some((l) => l.code === m[1]) ? m[1] : null
}

/** Map any BCP 47-ish tag to one of our locale codes (or null). */
export function matchLocale(tag: string): string | null {
  const t = tag.toLowerCase()
  if (t.startsWith('zh')) {
    return /hant|tw|hk|mo/.test(t) ? 'zh-hant' : 'zh-hans'
  }
  const base = t.split('-')[0]
  return UI_LOCALES.some((l) => l.code === base) ? base : null
}

/** ?lang= → /{code}/ path → saved choice → browser language → en.
 * URL signals win over the saved choice on purpose: opening the Russian
 * locale home (or clicking a ?lang=ru link from lingvanex.com) is a fresh,
 * explicit signal — it must override an older in-app selection, and it then
 * becomes the new saved choice. */
export function detectLocale(): string {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang')
    if (fromUrl) {
      const m = matchLocale(fromUrl)
      if (m) return m
    }
    const fromPath = pathLocale()
    if (fromPath) return fromPath
  } catch {
    // no URL access (tests) — fall through
  }
  try {
    const saved = localStorage.getItem(UI_LANG_STORAGE_KEY)
    if (saved && UI_LOCALES.some((l) => l.code === saved)) return saved
  } catch {
    // storage unavailable — fall through
  }
  for (const nav of navigator.languages ?? [navigator.language]) {
    if (!nav) continue
    const m = matchLocale(nav)
    if (m) return m
  }
  return 'en'
}
